import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icon, Row, Text } from '@/components/ui';
import { lockActiveIcon, lockIcon } from '@/assets';
import { Autocomplete } from '@/components/ui/Autocomplete';
import {
  ISSMap,
  MobileTextarea,
  MobileButton,
  MobileInput,
  MobileSelect,
} from '@/components/ui';
import { Layout, Header, useFooterContext } from '@/components/layout';
import { useSitesStore } from '@/features/sites/store';
import { useProjectsStore } from '@/features/projects/store';

// Constants
const PRIORITY = Array.from({ length: 10 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString(),
}));
console.log(PRIORITY);
interface CreateSitePageProps {
  searchParams?: { projectId?: number };
}

export const CreateSitePage = ({ searchParams }: CreateSitePageProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = searchParams || {};
  // Stores
  const { createSite, getSites } = useSitesStore(state => state.actions);
  const existingSites = useSitesStore(state => state.sites);
  const { getProjects } = useProjectsStore(state => state.actions);
  const { setFooterPlusButton } = useFooterContext();

  const [mapKey] = useState<string>('AIzaSyD2X1AoMDzxU-lgYxkcFQFH06kWfre_Z_4');
  const [nameError, setNameError] = useState<string>('');
  const [projectError, setProjectError] = useState<string>('');
  const [loadedAutocomplete, setLoadedAutocomplete] = useState<boolean>(false);
  const [isActiveLock, setIsActiveLock] = useState<boolean>(false);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);

  // Form state
  const [siteName, setSiteName] = useState<string>('');
  const [currentPriority, setCurrentPriority] = useState<number>(1);
  const [currentState, setCurrentState] = useState<string>('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number }>({
    lat: 0,
    lng: 0,
  });
  const [latitudeInput, setLatitudeInput] = useState<string>('0');
  const [longitudeInput, setLongitudeInput] = useState<string>('0');
  const [currentDescription, setDescription] = useState<string>('');
  const [currentRequiredChanges, setCurrentRequiredChanges] =
    useState<string>('');

  const CURRENT_STATE: { value: string; label: string }[] = [
    {
      value: t('current_state_01_select_param'),
      label: 'current_state_01_select_param',
    },
    {
      value: t('current_state_02_select_param'),
      label: 'current_state_02_select_param',
    },
    {
      value: t('current_state_03_select_param'),
      label: 'current_state_03_select_param',
    },
    {
      value: t('current_state_04_select_param'),
      label: 'current_state_04_select_param',
    },
    {
      value: t('current_state_05_select_param'),
      label: 'current_state_05_select_param',
    },
  ];

  // Effects
  useEffect(() => {
    setTimeout(() => {
      setLoadedAutocomplete(true);
    }, 1500);
  }, []);

  useEffect(() => {
    getProjects();
    // Load existing sites for the project to check for duplicates
    if (projectId) {
      getSites(projectId);
    }
    setCoordinates({ lat: 0, lng: 0 });
    // Hide footer plus button on create page
    setFooterPlusButton(false);

    return () => {
      setFooterPlusButton(false);
    };
  }, [projectId, getProjects, getSites, setFooterPlusButton]);

  // Handlers
  const onChangeName = (value: string) => {
    setSiteName(value);

    // Clear previous errors
    setNameError('');

    // Check for duplicate name
    if (value.trim().length > 0 && checkSiteNameDuplicate(value)) {
      setNameError('site_name_duplicate_error');
    }
    // Check name length validation
    else if (value.trim().length > 128) {
      setNameError('name_field_error_add_site_validation_text');
    }
  };

  const onChangeCurrentState = (value: string) => {
    setCurrentState(value);
  };

  const onChangeDescription = (value: string) => {
    setDescription(value);
  };

  const onChangeRequiredChanges = (value: string) => {
    setCurrentRequiredChanges(value);
  };

  const onChangeLatitude = (value: string) => {
    setLatitudeInput(value);
  };

  const onChangeLongitude = (value: string) => {
    setLongitudeInput(value);
  };

  const setValidLatitude = () => {
    if (!latitudeInput) return;
    const filteredValue = latitudeInput.replace(/[^0-9.,-]/g, '');
    const formattedValue = filteredValue.replace(',', '.');
    const lat = parseFloat(formattedValue);
    if (!isNaN(lat)) {
      setCoordinates({ lat, lng: coordinates.lng });
      setLatitudeInput(lat.toString());
    }
  };

  const setValidLongitude = () => {
    if (!longitudeInput) return;
    const filteredValue = longitudeInput.replace(/[^0-9.,-]/g, '');
    const formattedValue = filteredValue.replace(',', '.');
    const lng = parseFloat(formattedValue);
    if (!isNaN(lng)) {
      setCoordinates({ lat: coordinates.lat, lng });
      setLongitudeInput(lng.toString());
    }
  };

  const onChangePriority = (value: number) => {
    if (value < 1 || value > 10) return;
    setCurrentPriority(value);
  };

  // Validation
  const validationCheckName = (name: string) => {
    if (name.length < 1 || name.length > 128) {
      return false;
    }
    return true;
  };

  const checkSiteNameDuplicate = (name: string) => {
    const trimmedName = name.trim().toLowerCase();
    return existingSites.some(
      site => site.siteName.toLowerCase() === trimmedName
    );
  };

  const validationProject = (project: number | undefined) => {
    if (!project || project <= 0) {
      return false;
    }
    return true;
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return (
      siteName.trim().length > 0 && // Site name is required
      siteName.trim().length <= 128 && // Site name max length
      projectId &&
      projectId > 0 && // Project is required
      !nameError && // No name error
      !projectError // No project error
    );
  };

  const validation = () => {
    if (!validationCheckName(siteName)) {
      setNameError('name_field_error_add_site_validation_text');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return false;
    }

    if (checkSiteNameDuplicate(siteName)) {
      setNameError('site_name_duplicate_error');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return false;
    }

    if (!validationProject(projectId)) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      setProjectError('project_field_error_add_site_validation_text');
      return false;
    }

    return true;
  };

  const onChangeCoordinates = (position: { lat: number; lng: number }) => {
    // Ensure we have valid numbers
    const lat =
      typeof position.lat === 'number' && !isNaN(position.lat)
        ? position.lat
        : 0;
    const lng =
      typeof position.lng === 'number' && !isNaN(position.lng)
        ? position.lng
        : 0;

    const validPosition = { lat, lng };
    setCoordinates(validPosition);
    setLatitudeInput(lat.toString());
    setLongitudeInput(lng.toString());
  };

  const getCoordinates = () => {
    if ('geolocation' in navigator) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        position => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;

          if (!isNaN(latitude) && !isNaN(longitude)) {
            setCoordinates({ lat: latitude, lng: longitude });
            setLatitudeInput(latitude.toString());
            setLongitudeInput(longitude.toString());
          } else {
            console.error('Invalid coordinates from geolocation:', {
              latitude,
              longitude,
            });
          }
          setIsGettingLocation(false);
        },
        error => {
          console.error('Error getting location:', error.message);
          setIsGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      console.error('Geolocation API not supported.');
    }
  };

  const handleSaveSite = async () => {
    const isValid = validation();
    if (!isValid || !projectId) return;

    const siteData = {
      projectId: projectId,
      siteName: siteName,
      description: currentDescription,
      currentState: currentState,
      requiredChanges: currentRequiredChanges,
      priority: currentPriority,
      latitude: coordinates.lat,
      longitude: coordinates.lng,
    };

    const success = await createSite(siteData);

    const siteID = success.data.data.id;
    if (siteID) {
      navigate({
        to: `/projects/${projectId}/sites/${siteID}`,
        search: {},
      });
    } else {
      navigate({
        to: `/projects/${projectId}/sites`,
        search: {},
      });
    }
  };

  const toggleLock = () => {
    setIsActiveLock(!isActiveLock);
  };

  return (
    <Layout>
      <Box sx={{ position: 'relative' }}>
        <Header title={t('new_site_edit_site')} hideBackButton={false} />

        <Box mb={2}>
          <Box>
            <MobileInput
              label={`${t('site_name_edit_site')} *`}
              name='siteName'
              error={!!nameError}
              helperText={nameError}
              compact
              onChange={value => onChangeName(value.target.value)}
              value={siteName}
            />
          </Box>

          <Row spacing={'1.25rem'} sx={{ marginTop: '1.25rem' }}>
            <Box
              width='calc(30% - 0.625rem)'
              minWidth='calc(30% - 0.625rem)'
              maxWidth={'calc(30% - 0.625rem)'}
            >
              <MobileSelect
                title={t('priority_edit_site')}
                items={PRIORITY}
                error={false}
                noSelectLabel={t('priority_placeholder_edit_site')}
                onChange={value => onChangePriority(parseInt(value))}
                value={currentPriority.toString()}
              />
            </Box>
            <Box
              width='calc(70% - 0.625rem)'
              minWidth='calc(70% - 0.625rem)'
              maxWidth={'calc(70% - 0.625rem)'}
            >
              <MobileSelect
                title={t('current_state_edit_site')}
                items={CURRENT_STATE}
                noSelectLabel={t('current_state_placeholder_edit_site')}
                onChange={value => onChangeCurrentState(value)}
                value={currentState}
                error={false}
                translate
              />
            </Box>
          </Row>

          {mapKey && (
            <Row alignItems={'flex-end'}>
              <Box width={'100%'}>
                <Autocomplete
                  getCoordinates={getCoordinates}
                  mapKey={mapKey}
                  onChangeCoordinates={onChangeCoordinates}
                  isGettingLocation={isGettingLocation}
                />
              </Box>
              <Box
                onClick={toggleLock}
                sx={{ mx: '0.75rem', cursor: 'pointer' }}
              >
                <Icon src={isActiveLock ? lockActiveIcon : lockIcon} />
              </Box>
            </Row>
          )}

          {mapKey && loadedAutocomplete && coordinates && (
            <ISSMap
              mapKey={mapKey}
              value={coordinates}
              onChangeCoordinates={onChangeCoordinates}
              isActiveLock={isActiveLock}
            />
          )}

          <Box sx={{ display: 'flex', width: '100%' }}>
            <Box width='50%'>
              <MobileInput
                label={`${t('latitude_edit_site')}`}
                name='latitude'
                helperText={''}
                onChange={value => onChangeLatitude(value.target.value)}
                blur={setValidLatitude}
                value={latitudeInput}
                error={false}
              />
            </Box>
            <Box sx={{ width: '1.25rem' }} />
            <Box width='50%'>
              <MobileInput
                label={`${t('longitude_edit_site')}`}
                name='longitude'
                helperText={''}
                onChange={value => onChangeLongitude(value.target.value)}
                blur={setValidLongitude}
                value={longitudeInput}
                error={false}
              />
            </Box>
          </Box>

          <MobileInput
            label={`${t('required_changes_edit_site')}`}
            name='requiredChanges'
            error={false}
            helperText={''}
            onChange={value => onChangeRequiredChanges(value.target.value)}
            value={currentRequiredChanges}
            autoHeight
          />

          <MobileTextarea
            label={`${t('description_edit_site')}`}
            name='description'
            error={false}
            helperText={''}
            onChange={value => onChangeDescription(value.target.value)}
            value={currentDescription}
          />

          <MobileButton
            disabled={!isFormValid()}
            btnColor={isFormValid() ? '#1C5B9C' : '#CCCCCC'}
            onClick={handleSaveSite}
            sx={{
              opacity: isFormValid() ? 1 : 0.6,
              cursor: isFormValid() ? 'pointer' : 'not-allowed',
            }}
          >
            <Text sx={{ color: isFormValid() ? 'white' : '#666666' }}>
              {`${t('save_site')}`}
            </Text>
          </MobileButton>
        </Box>
      </Box>
    </Layout>
  );
};
